<template>
  <div>
    <nr-modal
      v-if="
        mode === MODES.COMPLETE ||
        mode === MODES.ERROR ||
        mode === MODES.PENDING
      "
      :title="modalTitle"
      :primary-button-text="primaryButtonText"
      :has-secondary-button="hasSecondaryButton"
      :secondary-button-text="secondaryButtonText"
      :show="true"
      size="medium"
      @on-close-click="onCancel"
      @on-primary-click="onPrimary"
      @on-secondary-click="onSecondary"
    >
      <template #default>
        <div
          v-if="mode === MODES.PENDING"
          class="pcc-center pcc-finalize-redaction-textarea"
        >
          <div class="finalize-redaction-text pcc-margin-top-50">
            {{ pendingText }}
          </div>
          <div class="pcc-load pcc-margin-top-8"></div>
        </div>

        <div
          v-else-if="mode === MODES.COMPLETE"
          class="pcc-finalize-redaction-textarea"
        >
          <div style="width: 560px; max-width: 90vw">
            <template v-if="rapidReview">
              <div
                class="finalize-redaction-text pcc-margin-top-8 pcc-margin-bottom-60"
              >
                By finalizing your document you will burn all redactions into
                the document and you will be unable to remove or edit them. All
                comments will be deleted. Are you sure you want to continue?

                <div class="redaction-control m-top-16">
                  <input
                    type="checkbox"
                    id="attach-redaction-log"
                    name="attach-redaction-log"
                    v-model="attachRedactionLog"
                  />
                  <label
                    for="attach-redaction-log"
                    class="nr-form-label finalize-text"
                  >
                    Attach redaction log to finalized document</label
                  >
                </div>
                <div class="redaction-control m-top-8">
                  <input
                    type="checkbox"
                    id="remove-metadata"
                    name="remove-metadata"
                    v-model="removeMetadata"
                  />
                  <label
                    for="remove-metadata"
                    class="nr-form-label finalize-text"
                    >Remove metadata</label
                  >
                  <NrTooltip :label="metadataTooltip()" position="right">
                    <i
                      class="far fa-question-circle finalize-text"
                      aria-hidden="true"
                    ></i>
                  </NrTooltip>
                </div>
              </div>
              <div
                v-if="marksWithoutReasons"
                class="finalize-redaction-warning"
              >
                This document contains redactions that do not have redaction
                reasons assigned. Assign redaction reasons for accurate
                redaction log generation.
              </div>
            </template>

            <template v-else>
              <div class="pcc-center complete-text">
                {{ languageText('burnInComplete') }}
              </div>
              <div class="redaction-control m-top-12 shim3-left">
                <input
                  type="checkbox"
                  id="attach-redaction-log"
                  name="attach-redaction-log"
                  v-model="attachRedactionLog"
                />
                <label for="attach-redaction-log" class="nr-form-label">
                  Attach redaction log to finalized document</label
                >
              </div>
              <div class="redaction-control m-top-8 shim3-left">
                <input
                  type="checkbox"
                  id="remove-metadata"
                  name="remove-metadata"
                  v-model="removeMetadata"
                />
                <label for="remove-metadata" class="nr-form-label"
                  >Remove metadata</label
                >
                <NrTooltip :label="metadataTooltip()" position="right">
                  <i class="far fa-question-circle" aria-hidden="true"></i>
                </NrTooltip>
              </div>

              <div class="pcc-center pcc-margin-top">
                <button class="button secondary" @click="onDownload">
                  <i class="fas fa-download" aria-hidden="true"></i>Download
                </button>
              </div>
            </template>
          </div>
        </div>

        <div
          v-else-if="mode === MODES.ERROR"
          class="pcc-center pcc-finalize-redaction-textarea finalize-redaction-error pcc-margin-top-50"
        >
          <div class="pcc-margin-top-8">
            {{ languageText('burnInError') }}
          </div>
        </div>
      </template>
    </nr-modal>
    <div style="display: none" aria-hidden="true">
      <button type="button" class="pcc-overlay-save-to-request">Save</button>
      <button type="button" class="pcc-overlay-cancel">Cancel</button>
      <button type="button" class="pcc-overlay-retry">Retry</button>
      <button type="button" class="pcc-overlay-download">Download</button>
    </div>
  </div>
</template>

<script>
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import { mapMutations } from 'vuex'

const MODES = Object.freeze({
  PENDING: 'pending',
  COMPLETE: 'complete',
  ERROR: 'error'
})

export default {
  name: 'PrizmdocFinalModal',
  components: { NrModal, NrTooltip },
  props: {
    mode: { type: String, required: true },
    marksWithoutReasons: { type: Boolean, default: false },

    rapidReview: {
      type: Boolean,
      default: () => window.location.href.indexOf('app/rapidreview') !== -1
    }
  },
  data() {
    return {
      attachRedactionLog: true,
      removeMetadata: false,
      MODES
    }
  },
  computed: {
    modalTitle() {
      if (this.mode === this.MODES.PENDING && !this.rapidReview) return ''
      if (this.mode === this.MODES.ERROR) return ''
      return 'Finalize Redactions'
    },
    hasSecondaryButton() {
      return this.mode === this.MODES.COMPLETE || this.mode === this.MODES.ERROR
    },
    primaryButtonText() {
      if (this.mode === this.MODES.COMPLETE) {
        return this.rapidReview ? 'Finalize document' : 'Save to Request'
      }
      if (this.mode === this.MODES.ERROR)
        return this.languageText('retryButton')
      if (this.mode === this.MODES.PENDING)
        return this.languageText('cancelButton')
      return ''
    },
    secondaryButtonText() {
      return this.languageText('cancelButton')
    },
    pendingText() {
      return this.rapidReview
        ? 'Creating the new document'
        : this.languageText('burnInPending')
    }
  },
  methods: {
    ...mapMutations('rapid_review', ['setCheckedDocIds']),
    metadataTooltip() {
      return `This option adds a step that converts the document to an image before creating the PDF,
      removing metadata like text and accessibility tags. This can help ensure redactions are burned in
      for documents with unusual formats. After using this, text-based redaction and search won't work
      unless an OCR process is applied.

      Note: Some jurisdictions treat document metadata as part of the official record and require its
      retention for legal or other purposes. You must ensure this feature complies with all applicable
      laws, regulations, and policies before use.`
    },
    languageText(key) {
      try {
        return (
          (window.PCCViewer &&
            window.PCCViewer.Language &&
            window.PCCViewer.Language.data &&
            window.PCCViewer.Language.data[key]) ||
          ''
        )
      } catch (e) {
        return ''
      }
    },
    onCancel() {
      const btn = document.querySelector('.pcc-overlay-cancel')
      if (btn) {
        btn.click()
      }
      this.$emit('cancel')
    },
    onPrimary() {
      if (this.mode === this.MODES.ERROR) {
        this.onRetry()
        return
      }
      if (this.mode === this.MODES.PENDING) {
        this.onCancel()
        return
      }
      const btn = document.querySelector('.pcc-overlay-save-to-request')
      if (btn) {
        btn.click()
      }
      this.$emit('primary')
      this.setCheckedDocIds([])
    },
    onSecondary() {
      this.onCancel()
    },
    onRetry() {
      const btn = document.querySelector('.pcc-overlay-retry')
      if (btn) {
        btn.click()
      }
      this.$emit('retry')
    },
    onDownload() {
      const btn = document.querySelector('.pcc-overlay-download')
      if (btn) {
        btn.click()
      }
      this.$emit('download')
    }
  }
}
</script>

<style scoped lang="scss">
.finalize-text {
  margin-left: 8px;
}
</style>
